import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDocumentMeta } from '../lib/use-document-meta.js';
import './Diagnosis.css';

export default function Diagnosis() {
  const { token } = useParams();
  const [state, setState] = useState({ status: 'loading', data: null, error: null });

  // Diagnosis pages are private (unguessable share token + robots.txt
  // Disallow). noindex set defensively so nothing escapes.
  useDocumentMeta({
    title: state.data?.first_name
      ? `${state.data.first_name}, after our session · Margin`
      : 'Your diagnosis · Margin',
    description: 'Three threads worth pulling, after a Margin diagnosis session.',
    noindex: true,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/get-diagnosis?token=${encodeURIComponent(token)}`);
        if (cancelled) return;
        if (res.status === 404) {
          setState({ status: 'not_found', data: null, error: null });
          return;
        }
        if (!res.ok) {
          setState({ status: 'error', data: null, error: `Could not load (${res.status})` });
          return;
        }
        const data = await res.json();
        setState({ status: 'ready', data, error: null });
      } catch (err) {
        if (cancelled) return;
        setState({ status: 'error', data: null, error: err.message || 'Network error' });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (state.status === 'loading') {
    return <div className="diagnosis-page diagnosis-state">Loading…</div>;
  }
  if (state.status === 'not_found') {
    return (
      <div className="diagnosis-page diagnosis-state">
        <h1 className="diagnosis-state-title">This diagnosis isn't available.</h1>
        <p className="diagnosis-state-body">
          The link may be wrong, or the diagnosis hasn't been sent yet.
          If you think this is a mistake, reply to the email Jason sent you.
        </p>
        <Link to="/" className="diagnosis-state-link">← margin.consulting</Link>
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className="diagnosis-page diagnosis-state">
        <h1 className="diagnosis-state-title">Something went wrong loading this page.</h1>
        <p className="diagnosis-state-body">{state.error}</p>
        <Link to="/" className="diagnosis-state-link">← margin.consulting</Link>
      </div>
    );
  }

  const { data } = state;
  const firstName = data.first_name?.trim();
  const issues = data.issues || [];
  const startingIdx = issues.findIndex((i) => i.is_starting_point);
  const orderedIssues = startingIdx > 0
    ? [issues[startingIdx], ...issues.filter((_, i) => i !== startingIdx)]
    : issues;

  return (
    <div className="diagnosis-page">
      <header className="diagnosis-header">
        <Link to="/" className="diagnosis-brand">margin</Link>
        <div className="diagnosis-meta">
          {data.business_name && <span>{data.business_name}</span>}
          {data.session_at && (
            <span className="diagnosis-meta-sep">·</span>
          )}
          {data.session_at && (
            <span>Session {formatSessionDate(data.session_at)}</span>
          )}
        </div>
      </header>

      <main className="diagnosis-main">
        <h1 className="diagnosis-title">
          {firstName ? `${firstName}, ` : ''}after our session
        </h1>

        {data.personal_note && (
          <section className="diagnosis-note">
            {data.personal_note.split('\n').map((p, i) =>
              p.trim() ? <p key={i}>{p}</p> : null
            )}
          </section>
        )}

        <section className="diagnosis-threads">
          <h2 className="diagnosis-h2">Three threads worth pulling</h2>

          <ol className="diagnosis-issue-list">
            {orderedIssues.map((iss, i) => (
              <li
                key={i}
                className={`diagnosis-issue${iss.is_starting_point ? ' diagnosis-issue--start' : ''}`}
              >
                {iss.is_starting_point && (
                  <span className="diagnosis-start-pill">Start here</span>
                )}
                {iss.title && <h3 className="diagnosis-issue-title">{iss.title}</h3>}
                {iss.description && (
                  <div className="diagnosis-issue-body">
                    {iss.description.split('\n').map((p, j) =>
                      p.trim() ? <p key={j}>{p}</p> : null
                    )}
                  </div>
                )}
              </li>
            ))}
          </ol>
        </section>

        <footer className="diagnosis-footer">
          <p className="diagnosis-footer-line">
            These are icebreakers, not conclusions. The next move is yours.
          </p>
          <p className="diagnosis-footer-sig">Jason</p>
        </footer>
      </main>
    </div>
  );
}

function formatSessionDate(iso) {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric',
    });
  } catch {
    return '';
  }
}
